import { useMemo, useState } from 'react'
import { Alert, Button, DatePicker, Space, Statistic, Typography } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatBytes, formatDateTime, utcTooltip } from '@/shared/lib/format'
import { defaultDayRange, tenantToday } from '@/shared/lib/dayRange'
import { formatMoneyValue } from '@/shared/lib/money'
import { translateError } from '@/shared/api/errors'
import { DataTable } from '@/shared/ui/table/DataTable'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'
import {
  downloadHistoryExport,
  fetchHistoryPage,
  type HistoryRow,
  type HistorySummary,
} from './historyApi'
import { performerLabel, kindLabel } from './historyLabels'
import { useStatementPrinter } from './useStatementPrinter'

const ISO_DAY = 'YYYY-MM-DD'

function amountColor(amount: number): string | undefined {
  if (amount > 0) return 'var(--ant-color-success)'
  if (amount < 0) return 'var(--ant-color-error)'
  return undefined
}

export function SubscriberHistory({ subscriberId, login }: { subscriberId: string; login: string }) {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(89))
  const [summary, setSummary] = useState<HistorySummary | null>(null)
  const period = useMemo(
    () => ({ from: range[0].format(ISO_DAY), to: range[1].format(ISO_DAY) }),
    [range],
  )
  const fetchPage = useMemo(() => fetchHistoryPage(subscriberId, period, setSummary), [subscriberId, period])
  const printer = useStatementPrinter(subscriberId)

  const presets = useMemo(() => {
    const today = tenantToday()
    return [
      { label: t('subscriber.history.preset.days30'), value: defaultDayRange(29) },
      { label: t('subscriber.history.preset.days90'), value: defaultDayRange(89) },
      { label: t('subscriber.history.preset.thisMonth'), value: [today.startOf('month'), today] as [Dayjs, Dayjs] },
      {
        label: t('subscriber.history.preset.lastMonth'),
        value: [
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month'),
        ] as [Dayjs, Dayjs],
      },
      { label: t('subscriber.history.preset.thisYear'), value: [today.startOf('year'), today] as [Dayjs, Dayjs] },
    ]
  }, [t])

  const columns = useMemo<ColumnsType<HistoryRow>>(
    () => [
      {
        key: 'at',
        title: t('subscriber.history.columns.at'),
        width: 170,
        render: (_, row) => <Typography.Text title={utcTooltip(row.at)}>{formatDateTime(row.at)}</Typography.Text>,
      },
      {
        key: 'operation',
        title: t('subscriber.history.columns.operation'),
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{kindLabel(t, row)}</Typography.Text>
            {row.packageName ? <Typography.Text type="secondary">{row.packageName}</Typography.Text> : null}
            {row.trafficBytes > 0 ? (
              <Typography.Text type="secondary">+{formatBytes(row.trafficBytes)}</Typography.Text>
            ) : null}
            {row.note ? <Typography.Text type="secondary">{row.note}</Typography.Text> : null}
          </Space>
        ),
      },
      {
        key: 'amount',
        title: t('subscriber.history.columns.amount'),
        align: 'end',
        width: 140,
        render: (_, row) => (
          <Typography.Text style={{ color: amountColor(row.amount.amount), whiteSpace: 'nowrap' }}>
            {row.amount.amount > 0 ? '+' : ''}
            {formatMoneyValue(row.amount)}
          </Typography.Text>
        ),
      },
      {
        key: 'balanceAfter',
        title: t('subscriber.history.columns.balanceAfter'),
        align: 'end',
        width: 140,
        render: (_, row) => (
          <Typography.Text
            strong
            type={row.balanceAfter.amount < 0 ? 'danger' : undefined}
            style={{ whiteSpace: 'nowrap' }}
          >
            {formatMoneyValue(row.balanceAfter)}
          </Typography.Text>
        ),
      },
      {
        key: 'reference',
        title: t('subscriber.history.columns.reference'),
        width: 160,
        render: (_, row) => (row.reference ? <Typography.Text copyable>{row.reference}</Typography.Text> : '—'),
      },
      {
        key: 'actor',
        title: t('subscriber.history.columns.actor'),
        width: 160,
        render: (_, row) => performerLabel(t, row),
      },
    ],
    [t],
  )

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <DatePicker.RangePicker
          value={range}
          presets={presets}
          allowClear={false}
          format="DD.MM.YYYY"
          disabledDate={(day) => day.isAfter(tenantToday(), 'day')}
          onChange={(value) => {
            if (value?.[0] && value[1]) setRange([value[0], value[1]])
          }}
        />
        <ServerExportButton
          size="middle"
          label={t('subscriber.history.export')}
          title={t('export.menuTitle')}
          run={(format, onProgress) => downloadHistoryExport(subscriberId, login, period, format, onProgress)}
        />
        <Button icon={<PrinterOutlined />} loading={printer.printing} onClick={() => void printer.print(period)}>
          {t('subscriber.history.print')}
        </Button>
      </Space>

      {printer.error ? <Alert type="error" showIcon message={translateError(printer.error, t)} /> : null}

      {summary ? (
        <Space size="large" wrap>
          <Statistic
            title={t('subscriber.history.openingOn', { day: dayjs(summary.from).format('DD.MM.YYYY') })}
            value={formatMoneyValue(summary.opening)}
          />
          <Statistic
            title={t('subscriber.history.closingOn', { day: dayjs(summary.to).format('DD.MM.YYYY') })}
            value={formatMoneyValue(summary.closing)}
            valueStyle={summary.closing.amount < 0 ? { color: 'var(--ant-color-error)' } : undefined}
          />
        </Space>
      ) : null}

      <DataTable<HistoryRow>
        queryKey={queryKeys.subscribers.history(subscriberId, period.from, period.to)}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="subscriber-history"
        urlPrefix="h"
        showTotal={false}
        scrollX={900}
        emptyTitle={t('subscriber.history.empty')}
        emptyDescription={t('subscriber.history.emptyHint')}
      />
      {printer.portal}
    </Space>
  )
}
